import { useEffect, useRef, useState } from 'react';
import { useShiftEvents } from '../../contexts/ShiftEventsProvider';
import type { ShiftTip, ShiftTipsResponse, ShiftTipAmounts, ShiftTipCalculateInput, ShiftTipAllocationMode } from 'shared-models';
import { shiftWorkforceService } from '../../services/workforce/shift-workforce.service';

export function useShiftTipsModalController(date: string) {
  const [overview, setOverview] = useState<ShiftTipsResponse | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [overrideDraft, setOverrideDraft] = useState<{ enabled: boolean; amounts: ShiftTipAmounts } | null>(null);
  const [policyOutdated, setPolicyOutdated] = useState(false);
  const [modeDraft,setModeDraft] = useState<ShiftTipAllocationMode | null>(null);
  const [roleLimit,setRoleLimit]=useState(20);
  const mutationLock = useRef(false);
  const generation = useRef(0);
  const loading = useRef(false);
  const liveTips = useRef(new Map<number, ShiftTip | null>());
  const liveOverview = useRef<ShiftTipsResponse | null>(null);

  const loadTips = async () => {
    const requestGeneration = ++generation.current;
    loading.current = true;
    liveTips.current.clear();
    liveOverview.current = null;
    setError(null);
    try {
      const result = await shiftWorkforceService.getTips(date);
      if (requestGeneration !== generation.current) return;
      const snapshot = liveOverview.current ?? result;
      const merged = new Map(snapshot.tips.map((tip) => [tip.id, tip]));
      for (const [id, tip] of liveTips.current) {
        if (tip && tip.date === date) merged.set(id, tip);
        else merged.delete(id);
      }
      setOverview({ ...snapshot, tips: [...merged.values()] });
      setPolicyOutdated(false);
    } catch {
      if (requestGeneration === generation.current) setError('לא ניתן לטעון את נתוני הטיפים.');
    } finally {
      if (requestGeneration === generation.current) {
        loading.current = false;
        liveTips.current.clear();
      }
    }
  };

  const eventsReady = useShiftEvents((event) => {
    if (event.type === 'tip.policyChanged') {
      setPolicyOutdated(true);
    } else if ((event.type==='schedule.publicationChanged' || event.type==='cost.upserted' || event.type==='cost.removed' || event.type==='costValue.upserted' || event.type==='costValue.removed')
      && (modeDraft ?? overview?.tips.find(tip => tip.typeShift==='daily')?.allocationMode ?? 'shared')==='shared') {
      setPolicyOutdated(true);
    } else if (event.type === 'tip.sourceChanged' && event.overview.source.date === date) {
      if (loading.current) {
        liveOverview.current = event.overview;
        liveTips.current.clear();
      }
      setOverview(event.overview);
      setPolicyOutdated(false);
    } else if (event.type === 'tip.upserted' && event.tip.date === date) {
      if (loading.current) liveTips.current.set(event.tip.id, event.tip);
      setOverview((current) => current && ({ ...current, tips: [...current.tips.filter((tip) => tip.id !== event.tip.id), event.tip] }));
    } else if (event.type === 'tip.removed') {
      if (loading.current) liveTips.current.set(event.id, null);
      setOverview((current) => current && ({ ...current, tips: current.tips.filter((tip) => tip.id !== event.id) }));
    } else if (event.type === 'sse.resync') void loadTips();
  });

  useEffect(() => {
    let active = true;
    setOverview(null);
    setOverrideDraft(null);
    setModeDraft(null);
    setRoleLimit(20);
    setPolicyOutdated(false);
    queueMicrotask(() => { if (active && eventsReady) void loadTips(); });
    return () => { active = false; generation.current += 1; };
  }, [date, eventsReady]);

  const saveTipChange = async (key: string, action: () => Promise<unknown>) => {
    if (mutationLock.current) return;
    mutationLock.current = true;
    const requestGeneration = generation.current;
    setBusy(key);
    setError(null);
    try {
      await action();
    } catch (requestError) {
      if (requestGeneration === generation.current) setError(requestError instanceof Error ? requestError.message : 'הפעולה נכשלה.');
    } finally {
      mutationLock.current = false;
      setBusy(null);
    }
  };

  const savedTip = overview?.tips.find(tip => tip.typeShift === 'daily');
  const allocationMode = modeDraft ?? savedTip?.allocationMode ?? 'shared';
  const ownSource = overview?.ownSource?.date===date ? overview.ownSource : null;
  const canCalculate = allocationMode==='own' ? Boolean(ownSource?.canCalculate && ownSource.employees.length)
    : Boolean(overview?.source.workedMinutes && !overview.source.openAttendanceCount && !overview.source.unresolvedRoleCount && !overview.source.roleSourceOverflow);
  const overrideEnabled = overrideDraft?.enabled ?? Boolean(savedTip?.manualOverride);
  const amounts: Required<ShiftTipAmounts> = overrideEnabled ? {
    tipCash: overrideDraft?.amounts.tipCash ?? savedTip?.manualOverride?.tipCash ?? overview?.source.tipCash ?? '0',
    tipCredit: overrideDraft?.amounts.tipCredit ?? savedTip?.manualOverride?.tipCredit ?? overview?.source.tipCredit ?? '0',
    tipOther: overrideDraft?.amounts.tipOther ?? savedTip?.manualOverride?.tipOther ?? overview?.source.tipOther ?? '0',
  } : { tipCash: overview?.source.tipCash ?? '0', tipCredit: overview?.source.tipCredit ?? '0', tipOther: overview?.source.tipOther ?? '0' };
  const wholeAmounts = Object.values(amounts).every(value => /^\d{1,10}(?:\.00)?$/.test(value));
  const onCalculateTip = () => {
    if (!overview || overview.source.date !== date || policyOutdated || !canCalculate || savedTip?.isLocked) return;
    if (allocationMode==='own') return saveTipChange('daily-calculate',() => shiftWorkforceService.calculateTip(date,'daily',{
      fingerprint:ownSource!.fingerprint,allocationMode:'own',
    }));
    if (!wholeAmounts) return;
    const payload: ShiftTipCalculateInput = { fingerprint: overview.source.fingerprint };
    if (savedTip?.allocationMode==='own') payload.allocationMode='shared';
    if (!overrideEnabled && savedTip?.manualOverride) payload.override = null;
    else if (overrideEnabled) {
      const changes: ShiftTipAmounts = {};
      for (const key of ['tipCash', 'tipCredit', 'tipOther'] as const) {
        if (Number(amounts[key]) !== Number(savedTip?.manualOverride?.[key] ?? overview.source[key])) changes[key] = amounts[key];
      }
      if (Object.keys(changes).length) payload.override = changes;
    }
    return saveTipChange('daily-calculate', () => shiftWorkforceService.calculateTip(date, 'daily', payload));
  };
  const onToggleTipLock = (tip: ShiftTip) =>
    saveTipChange(`${tip.typeShift}-lock`, () => shiftWorkforceService.setTipLocked(tip.id, !tip.isLocked));
  const onClearCalculation = (tip: ShiftTip) =>
    saveTipChange(`${tip.typeShift}-clear`, () => shiftWorkforceService.clearTip(tip.id));
  const onRoleCorrection=(attendanceId:number,rolePk:number | null) => {
    if (!overview || overview.source.date!==date || allocationMode!=='shared' || savedTip?.isLocked || policyOutdated) return;
    const period=overview.source.rolePeriods?.find(row => row.attendanceId===attendanceId);
    if (!period || (rolePk===null ? period.source!=='manager' : period.rolePk===rolePk)) return;
    return saveTipChange(`role-${attendanceId}`,async () => {
      setPolicyOutdated(true);
      await shiftWorkforceService.setTipRole(date,attendanceId,{fingerprint:overview.source.fingerprint,rolePk});
    });
  };

  return {
    allocationMode,ownSource,canCalculate,onRoleCorrection,
    rolePeriods:overview?.source.rolePeriods?.slice(0,roleLimit) ?? [],
    hasMoreRolePeriods:Boolean(overview?.source.rolePeriods && overview.source.rolePeriods.length>roleLimit),
    onMoreRolePeriods:() => setRoleLimit(limit => limit+20),
    onChangeMode:(mode:ShiftTipAllocationMode) => {if (!mutationLock.current && !savedTip?.isLocked) setModeDraft(mode);},
    overrideEnabled, amounts, wholeAmounts, policyOutdated,
    onToggleOverride: (enabled: boolean) => { if (!mutationLock.current && !savedTip?.isLocked) setOverrideDraft({ enabled, amounts: {} }); },
    onChangeOverride: (key: keyof ShiftTipAmounts, value: string) => {
      if (!mutationLock.current && !savedTip?.isLocked) setOverrideDraft(current => {
        const changes = { ...current?.amounts, [key]: value };
        if (value !== '' && Number(value) === Number(savedTip?.manualOverride?.[key] ?? overview?.source[key])) delete changes[key];
        return { enabled: true, amounts: changes };
      });
    },
    onRefresh: () => { if (!mutationLock.current) void loadTips(); },
    onCalculateTip,
    onToggleTipLock,
    onClearCalculation,
    source: overview?.source.date === date ? overview.source : null,
    tips: overview?.source.date === date ? overview.tips : [],
    busy,
    error,
  };
}
